import { useCallback, useRef, useState } from 'react';
import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import { LuCalendar, LuRepeat } from 'react-icons/lu';
import type { PlanNodeOut } from '@/client';
import ReviewFact from '@/components/maintenance/review/ReviewFact';
import ScheduleChip from '@/components/maintenance/node/ScheduleChip';
import ProposalScheduleEditor from '@/components/maintenance/review/ProposalScheduleEditor';
import FrequencySelector from '@/components/maintenance/sheets/FrequencySelector';
import DateChoiceField from '@/components/maintenance/sheets/DateChoiceField';
import { formatDate, formatShortDate } from '@/utils/dateUtils';
import { isSameFrequency } from '@/utils/maintenance/frequencyUtils';
import { PlanScheduleKinds } from '@/components/maintenance/model/planVocabulary';

interface ProposalFrequencyProps {
	node: PlanNodeOut;
	/** Whether the card's own `date.basis` may be shown — see ProposalCard. */
	canShowDateBasis: boolean;
	/**
	 * Take a corrected cadence, which then rides on the keep.
	 *
	 * Absent where the card cannot be rescheduled, which leaves the cadence as plain text. The
	 * caller holds the value: a card can be corrected, scrolled past, and come back to.
	 */
	onCadenceChange?: (cadence: string | undefined) => void;
	/** The correction so far, or undefined while the card still reads as the plan proposed it. */
	cadence?: string;
	/** Take a corrected first-due date, which rides on the same keep as the cadence. */
	onAnchorDateChange?: (anchorDate: string | undefined) => void;
	anchorDate?: string;
}

type ScheduleEditor = 'cadence' | 'date';

/**
 * How long a chip stays shut after its own editor dismissed itself.
 *
 * A press on an open chip reaches two handlers: the popover treats it as a press outside and closes
 * on pointer-down, then the chip's own click arrives a few milliseconds later and would open it
 * straight back up — which is why the editor could not be shut by pressing the thing that opened it.
 * Long enough to cover that pair, short enough that a second, deliberate press opens it again.
 */
const REOPEN_AFTER_DISMISS_MS = 250;

/**
 * How often a proposed card repeats, and the chance to correct it before keeping.
 *
 * This is where a household notices a wrong cadence — the line already says "(inferred)" when the
 * plan guessed it. Correcting it here rather than after keeping means one write instead of two, and
 * no window where the card is re-cadenced but not yet vetted.
 */
const ProposalFrequency = (props: ProposalFrequencyProps) => {
	const { node, canShowDateBasis, onCadenceChange, cadence, onAnchorDateChange, anchorDate } = props;
	const [editing, setEditing] = useState<ScheduleEditor | null>(null);
	// Which editor last dismissed itself, and when. Held per editor so that pressing the other chip
	// still moves straight to it rather than being swallowed as the same press.
	const dismissed = useRef<{ editor: ScheduleEditor; at: number } | null>(null);

	const setEditorOpen = useCallback((editor: ScheduleEditor, open: boolean) => {
		if (!open) dismissed.current = { editor, at: Date.now() };
		setEditing(open ? editor : null);
	}, []);

	const toggleEditor = useCallback(
		(editor: ScheduleEditor) =>
			setEditing((current) => {
				if (current === editor) return null;
				const last = dismissed.current;
				const isSamePress = last?.editor === editor && Date.now() - last.at < REOPEN_AFTER_DISMISS_MS;
				return isSamePress ? current : editor;
			}),
		[],
	);
	/**
	 * An as-needed card has nothing to change here, so both chips read as plain statements.
	 *
	 * There is no date to move — the endpoint forces `next_due` null because there is no cadence to
	 * derive one from — and no interval to correct, since what it says is a `timing_label` rather
	 * than a cadence. Offering the editors put a date picker on a card whose whole point is that it
	 * has no date, and the write behind them has nothing to carry.
	 */
	const isAsNeeded = node.schedule?.kind === PlanScheduleKinds.AsNeeded || node.schedule?.is_recurring === false;
	const editCadence = isAsNeeded ? undefined : onCadenceChange;
	const editAnchorDate = isAsNeeded ? undefined : onAnchorDateChange;
	const proposed = node.schedule?.cadence;
	const cadenceText = proposed?.text ? `${proposed.text}${proposed.is_inferred ? ' (inferred)' : ''}` : null;
	const scheduleText = cadence ?? cadenceText ?? node.schedule?.timing_label;
	const due = anchorDate ?? node.next_due;
	// The chip carries the date alone; "First due" is said once beside it. Inside the pill the words
	// were the wider half of a control whose whole job is the date, and they repeated on every card
	// down a pass.
	const dueValue = due ? formatDate(due) : 'No date yet';
	const compactDueValue = due ? formatShortDate(due) : 'No date';

	if (!scheduleText && !node.next_due) return null;

	const dateEditor = (
		<ProposalScheduleEditor
			title="Change first due date"
			isOpen={editing === 'date'}
			onOpenChange={(open) => setEditorOpen('date', open)}
			onUndo={anchorDate && editAnchorDate ? () => editAnchorDate(undefined) : undefined}
			trigger={
				<ScheduleChip
					icon={LuCalendar}
					value={dueValue}
					mobileValue={compactDueValue}
					// The fact's own label names it now, so the chip says only the date. The accessible
					// name still has to carry both — a control named "15 January 2027" says nothing about
					// what pressing it does.
					ariaLabel={`Change first due date, currently ${dueValue}`}
					tooltip="Change date"
					tooltipDisabled={editing === 'date'}
					onSelect={editAnchorDate ? () => toggleEditor('date') : undefined}
				/>
			}
		>
			{editAnchorDate ? (
				<DateChoiceField
					value={anchorDate ?? node.next_due ?? ''}
					onChange={(next) => {
						editAnchorDate(next === node.next_due ? undefined : next || undefined);
						// Picking the day is the answer, so the editor closes on it rather than waiting
						// to be dismissed. Only on a real date: clearing the field is not a decision.
						if (next) setEditorOpen('date', false);
					}}
					changeLabel="Change first due date"
				/>
			) : null}
		</ProposalScheduleEditor>
	);

	/**
	 * Two facts, not one row of two chips.
	 *
	 * How often a card repeats and when it first falls due are separate answers, and a reader
	 * comparing cards down a pass is reading one column at a time — which the shared label column
	 * gives them only if each answer has a label of its own.
	 *
	 * Side by side where they fit and stacked where they do not, decided by wrapping rather than by a
	 * breakpoint: the same card is drawn in the pass's reading column and in a narrow drawer, so the
	 * viewport says nothing useful about the room this row actually has.
	 *
	 * Frequency uses the contact review's 72px label column. First due sizes its label to the words,
	 * since it sits beside Frequency rather than joining the card's vertical value column. Refusing
	 * to shrink is what makes the pair wrap onto separate lines when the room runs out.
	 */
	return (
		<Flex wrap="wrap" columnGap={4} rowGap={2.5}>
			{scheduleText ? (
				<Box flexShrink={0}>
					<ReviewFact label="Frequency" alignToControl>
						<ProposalScheduleEditor
							title="Change frequency"
							showDone
							isOpen={editing === 'cadence'}
							onOpenChange={(open) => setEditorOpen('cadence', open)}
							onUndo={cadence && editCadence ? () => editCadence(undefined) : undefined}
							trigger={
								<ScheduleChip
									icon={LuRepeat}
									value={scheduleText}
									mobileValue={scheduleText}
									ariaLabel={`Change frequency, currently ${scheduleText}`}
									tooltip="Change frequency"
									tooltipDisabled={editing === 'cadence'}
									onSelect={editCadence ? () => toggleEditor('cadence') : undefined}
								/>
							}
						>
							{editCadence ? (
								<FrequencySelector
									value={cadence ?? proposed?.text ?? ''}
									onChange={(next) => {
										// Choosing the cadence the card already has is not a correction.
										editCadence(isSameFrequency(next, proposed?.text) ? undefined : next);
									}}
									isRecurring={node.schedule?.is_recurring !== false}
									// Whether a card repeats at all is a different decision from how often,
									// and the review card only offers the second.
									showToggle={false}
									framed={false}
									inline
									// The popover and the sheet are the field's own box: no gutter to indent
									// from, and no second row to spend on the label.
									compact
									fitCountMenuToAvailableSpace
								/>
							) : null}
						</ProposalScheduleEditor>
					</ReviewFact>
				</Box>
			) : null}
			<Box flexShrink={0}>
				<ReviewFact label="First due" alignToControl fitLabel>
					<VStack align="stretch" gap={2}>
						{dateEditor}
						{node.date?.is_estimated && node.date.basis && canShowDateBasis ? (
							// TODO(api-date-basis-source): `date.basis` is free text, which is why a
							// document-derived card withholds it entirely. Once the plan API exposes its document
							// id and cited pages, filter on that id and render it as a link to that record.
							// Visible text, not the chip's tooltip: where a date came from is the reason to
							// question it, and a household is not going to hover a pill to find that out.
							<Text textStyle="text.xs" color="fg.subtle">
								Date estimated from {node.date.basis}
							</Text>
						) : null}
					</VStack>
				</ReviewFact>
			</Box>
		</Flex>
	);
};

export default ProposalFrequency;
